import type { Schema } from '#core/lib/expr/schema/types.ts';
import { type Cell, LazyDataset } from '#core/lib/expr/value.ts';

/**
 * Single source of truth for "this view property is a CogniteTimeSeries
 * reference" — the predicate that makes a column a `nested-dataset`
 * (TS-ref) column.
 *
 * Consumed by `zodTypeTagToColumnType` (the schema side) AND, by
 * extension, by `projectCell` (the cell side, which only enters its
 * TS-ref branch when the schema already says `nested-dataset`). Keeping
 * the decision here means the two sides can never re-derive it
 * independently and drift — the divergence that produced
 * `liftPerCell`'s "nested-dataset column invariant violated" throw.
 *
 * Matches any `referencedSchema` whose name ends in `TimeSeries`
 * (e.g. `CogniteTimeSeries`), tolerating namespace prefixes the view
 * generator may emit.
 */
export const isTimeSeriesRef = (referencedSchema?: string): boolean =>
  referencedSchema?.endsWith('TimeSeries') ?? false;

/**
 * Extract the time-series externalId from a raw CDF property value,
 * accepting BOTH encodings an instance query can return for a
 * CogniteTimeSeries reference:
 *
 *   - a bare externalId string
 *     (`"DJ0078-L:WellOutputs:priorityOilRateStd"`)
 *   - a `{ space?, externalId }` instance-reference object
 *
 * Returns `null` for genuinely absent / malformed values (null,
 * undefined, empty string, missing or non-string externalId). The
 * single source of truth for "raw CDF value → TS externalId", used by
 * `projectCell`.
 */
export const extractTsRefExternalId = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value.length > 0 ? value : null;
  }
  if (value !== null && typeof value === 'object' && 'externalId' in value) {
    const { externalId } = value as { externalId: unknown };
    return typeof externalId === 'string' && externalId.length > 0 ? externalId : null;
  }
  return null;
};

/**
 * Construct a TS-ref cell: a `LazyDataset` over a `ts-ref` source that
 * the chat plugin's `tsRefFetcher` resolves to datapoints at
 * materialisation.
 *
 * The single source of truth for the `nested-dataset` cell shape —
 * `projectCell` builds TS-ref cells only through this, so "schema kind
 * = nested-dataset" implies "cell is a `Dataset`" by construction and
 * the `liftPerCell` invariant can never be violated by a schema/payload
 * mismatch.
 */
export const tsRefCell = (externalId: string, innerSchema: Schema): Cell =>
  new LazyDataset({ source: { kind: 'ts-ref', externalId }, ops: [] }, innerSchema) as Cell;
